// Workspace admin: general (name), meta repos (add, refresh, remove), members and invites, and,
// for owners only, Fleet and CI (P-004: GitHub App, hosts, runners; pages/FleetSettings; alerts, pages/AlertsSettings).
// Owners change things; viewers see the workspace part read-only. The API decides: its 403s and
// refusal messages (e.g. an unsupported repos.yaml format) are shown as it words them.
import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, errorMessage, type Invite, type Member, type MetaRepo, type Role } from '../api/client';
import { LabelTag, pulseClass, tagClass } from '../components/Bits';
import { Icon } from '../components/Icon';
import { Shell } from '../components/Shell';
import { Button, buttonClass, Card, Input, PageHeader, Select, Table, Td, Th, cn, mono } from '../components/ui';
import { findWorkspace, useSession } from '../lib/session';
import { signInEmailLabel, signInProviderName } from '../lib/signin';
import { ago } from '../lib/time';
import { useLoad } from '../lib/useLoad';
import { AlertsSection } from './AlertsSettings';
import { GitHubAppSection, HostsSection, RunnersSection, useFleet } from './FleetSettings';
import { NotFoundPage } from './NotFoundPage';

type Outcome = { ok: boolean; text: string } | null;

const H2 = 'm-0 mb-1 text-[14px] font-semibold';
const SUB = 'm-0 mb-3.5 text-[12.5px] text-ink-muted';
const SECTION = 'min-w-0 scroll-mt-[72px]';
const FORM_INPUT = 'min-w-[180px] max-w-[300px] flex-1 phone:min-w-0 phone:max-w-none phone:basis-full';
const ROLE = 'min-w-0';
const FORM = 'mt-3 flex flex-wrap gap-2';
const K = 'w-[92px] flex-none text-ink-muted';
const WHO = 'flex min-w-0 items-center gap-2.5 [overflow-wrap:anywhere]';
const SMALL = 'block text-[11.5px] text-ink-muted';
const AVATAR =
  'grid size-[26px] flex-none place-items-center rounded-full bg-accent-faded text-[10.5px] font-semibold text-accent-display';

function Status({ outcome, className }: { outcome: Outcome; className?: string }) {
  if (!outcome) return null;
  return (
    <p
      className={cn(
        'mb-0 mt-2.5 flex items-center gap-1.5 text-[12.5px]',
        outcome.ok ? 'text-checks-pass-ink' : 'font-medium text-checks-fail-ink',
        className,
      )}
      role={outcome.ok ? 'status' : 'alert'}
    >
      {!outcome.ok && <Icon name="warn" small />}
      {outcome.text}
    </p>
  );
}

function General({ ws, name, owner, onSaved }: { ws: number; name: string; owner: boolean; onSaved: () => void }) {
  const [value, setValue] = useState(name);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<Outcome>(null);
  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    const { data, error, response } = await api.PATCH('/api/workspaces/{ws_id}', {
      params: { path: { ws_id: ws } },
      body: { name: value.trim() },
    });
    setBusy(false);
    if (data) {
      setOutcome({ ok: true, text: 'Saved.' });
      onSaved();
    } else setOutcome({ ok: false, text: errorMessage(error, response.status) });
  }
  return (
    <section id="general" aria-labelledby="h-general" className={SECTION}>
      <h2 id="h-general" className={H2}>
        General
      </h2>
      <p className={SUB}>The workspace's name.</p>
      <form
        className="grid grid-cols-[160px_1fr] items-center gap-4 border-y border-hairline py-3 phone:grid-cols-[minmax(0,1fr)] phone:gap-1.5"
        onSubmit={save}
      >
        <label className="text-[12.5px] font-medium text-ink-display" htmlFor="ws-name">
          Workspace name
        </label>
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          {owner ? (
            <>
              <Input
                id="ws-name"
                sans
                className="max-w-[340px]"
                value={value}
                maxLength={100}
                required
                onChange={(e) => setValue(e.target.value)}
              />
              <Button type="submit" variant="primary" disabled={busy || !value.trim() || value.trim() === name}>
                Save
              </Button>
            </>
          ) : (
            <span id="ws-name">{name}</span>
          )}
        </div>
      </form>
      <Status outcome={outcome} />
    </section>
  );
}

function RepoRow({ repo, owner, onChanged }: { repo: MetaRepo; owner: boolean; onChanged: () => void }) {
  const [confirm, setConfirm] = useState(false);
  const [outcome, setOutcome] = useState<Outcome>(null);
  async function refresh() {
    const { data, error, response } = await api.POST('/api/repos/{repo_id}/refresh', {
      params: { path: { repo_id: repo.id } },
    });
    setOutcome(data ? { ok: true, text: `Refresh queued (${data.queued} job${data.queued === 1 ? '' : 's'}).` } : { ok: false, text: errorMessage(error, response.status) });
  }
  async function remove() {
    const { error, response } = await api.DELETE('/api/repos/{repo_id}', { params: { path: { repo_id: repo.id } } });
    if (response.ok) onChanged();
    else {
      setConfirm(false);
      setOutcome({ ok: false, text: errorMessage(error, response.status) });
    }
  }
  const s = repo.sync;
  return (
    <Card as="article" padding="none" className="[&+article]:mt-2.5" data-repo={repo.full_name}>
      <div className="flex flex-wrap items-center gap-2.5 px-3.5 py-3">
        <Icon name="gh" />
        <a
          className="font-mono text-[12.5px] font-semibold text-ink-display [overflow-wrap:anywhere]"
          href={repo.html_url}
          target="_blank"
          rel="noopener noreferrer"
        >
          {repo.full_name}
        </a>
        {repo.format !== null && <span className={tagClass('kind')}>format {repo.format}</span>}
        <span className="ml-auto flex items-center gap-2 text-[11.5px] text-ink-muted">
          <span className={pulseClass(s.state)} aria-hidden="true" />
          {s.state === 'ok' && `synced ${ago(s.synced_at)}`}
          {s.state === 'pending' && 'first sync pending'}
          {s.state === 'error' && (
            <span className="font-semibold text-checks-fail-ink">{s.settings_error?.message ?? s.error ?? 'sync failed'}</span>
          )}
        </span>
        {owner && (
          <span className="flex flex-wrap gap-1.5">
            <Link className={buttonClass('primary')} to={`/w/${repo.workspace_id}/settings/repos/${repo.id}`}>
              <Icon name="gear" small />
              Edit settings
            </Link>
            <Button onClick={refresh}>
              <Icon name="refresh" small />
              Refresh
            </Button>
            {confirm ? (
              <>
                <Button variant="danger" onClick={remove}>
                  Remove {repo.full_name}
                </Button>
                <Button variant="ghost" onClick={() => setConfirm(false)}>
                  Keep
                </Button>
              </>
            ) : (
              <Button variant="ghost" onClick={() => setConfirm(true)}>
                Remove
              </Button>
            )}
          </span>
        )}
      </div>
      <div className="grid gap-1.5 rounded-b-lg border-t border-hairline bg-surface pb-3 pl-[38px] pr-3.5 pt-2.5 phone:pl-3.5 [&>div]:flex [&>div]:min-w-0 [&>div]:flex-wrap [&>div]:items-center [&>div]:gap-2.5 [&>div]:text-[12px]">
        {repo.code_repos.map((c) => (
          <div key={c.key}>
            <span className={K}>code repo</span>
            <span className={mono}>{c.full_name}</span>
            <span className={tagClass('kind')}>base {c.base_branch}</span>
            <span className={tagClass('kind')}>release {c.default_branch}</span>
          </div>
        ))}
        <div>
          <span className={K}>issues from</span>
          <span className={mono}>{repo.issues_repo ?? '—'}</span>
          {Object.values(repo.labels)
            .filter(Boolean)
            .map((l) => (
              <LabelTag key={l} name={l} roles={repo.labels} />
            ))}
        </div>
        <div>
          <span className={K}>in-flight cap</span>
          <span className={mono}>
            {repo.unreported.count} of {repo.unreported.cap ?? '–'}
          </span>
          <span className="text-ink-muted">read from repos.yaml</span>
        </div>
      </div>
      <Status outcome={outcome} className="m-0 px-3.5 py-2" />
    </Card>
  );
}

function Repos({ ws, owner }: { ws: number; owner: boolean }) {
  const [repos, reload] = useLoad(
    () => api.GET('/api/workspaces/{ws_id}/repos', { params: { path: { ws_id: ws }, query: { limit: 200 } } }),
    `${ws}`,
  );
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<Outcome>(null);
  async function add(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setOutcome(null);
    const { data, error, response } = await api.POST('/api/workspaces/{ws_id}/repos', {
      params: { path: { ws_id: ws } },
      body: { full_name: name.trim() },
    });
    setBusy(false);
    if (data) {
      setName('');
      setOutcome({ ok: true, text: `Added ${data.full_name}; its first sync is queued.` });
      reload();
    } else setOutcome({ ok: false, text: errorMessage(error, response.status) });
  }
  return (
    <section id="repos" aria-labelledby="h-repos" className={SECTION}>
      <h2 id="h-repos" className={H2}>
        Meta repos
      </h2>
      <p className={SUB}>
        Each meta repo's <span className={mono}>repos.yaml</span> names the code repos whose issues and pull requests show
        on the board.
      </p>
      {repos.state === 'loading' && <p className="text-ink-muted">Loading…</p>}
      {repos.state === 'error' && <Status outcome={{ ok: false, text: repos.message }} />}
      {repos.state === 'ok' && repos.data.repos.length === 0 && <p className="text-ink-muted">No meta repos yet.</p>}
      {repos.state === 'ok' && repos.data.repos.map((r) => <RepoRow key={r.id} repo={r} owner={owner} onChanged={reload} />)}
      {owner && (
        <form className={FORM} onSubmit={add}>
          <label className="sr-only" htmlFor="add-repo">
            Meta repo (owner/name)
          </label>
          <Input
            id="add-repo"
            className={FORM_INPUT}
            placeholder="owner/name, e.g. acme/acme-meta"
            value={name}
            required
            onChange={(e) => setName(e.target.value)}
          />
          <Button type="submit" disabled={busy || !name.trim()}>
            <Icon name="plus" />
            Add meta repo
          </Button>
          <span className="w-full text-[11.5px] text-ink-muted">
            The GitHub App must be installed on the repo, and its <span className={mono}>repos.yaml</span> must say{' '}
            <span className={mono}>format: 1</span>.
          </span>
        </form>
      )}
      <Status outcome={outcome} />
    </section>
  );
}

function initials(name: string): string {
  return (
    name
      .split(/[\s@._-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase())
      .join('') || '?'
  );
}

function Members({ ws, owner, me }: { ws: number; owner: boolean; me: number }) {
  const [list, reload] = useLoad(
    () => api.GET('/api/workspaces/{ws_id}/members', { params: { path: { ws_id: ws }, query: { limit: 200 } } }),
    `${ws}`,
  );
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('viewer');
  const [outcome, setOutcome] = useState<Outcome>(null);

  async function add(e: FormEvent) {
    e.preventDefault();
    setOutcome(null);
    const { data, error, response } = await api.POST('/api/workspaces/{ws_id}/members', {
      params: { path: { ws_id: ws } },
      body: { email: email.trim(), role },
    });
    if (data) {
      setEmail('');
      setOutcome({
        ok: true,
        text: data.member
          ? `${data.member.name ?? data.member.email} is now a ${data.member.role}.`
          : `Invited ${data.invite?.email}; they join at their first sign-in.`,
      });
      reload();
    } else setOutcome({ ok: false, text: errorMessage(error, response.status) });
  }
  async function changeRole(m: Member, r: Role) {
    const { data, error, response } = await api.PATCH('/api/workspaces/{ws_id}/members/{user_id}', {
      params: { path: { ws_id: ws, user_id: m.user_id } },
      body: { role: r },
    });
    setOutcome(data ? { ok: true, text: `${m.name ?? m.email} is now a ${r}.` } : { ok: false, text: errorMessage(error, response.status) });
    reload();
  }
  async function removeMember(m: Member) {
    const { error, response } = await api.DELETE('/api/workspaces/{ws_id}/members/{user_id}', {
      params: { path: { ws_id: ws, user_id: m.user_id } },
    });
    setOutcome(response.ok ? { ok: true, text: `Removed ${m.name ?? m.email}.` } : { ok: false, text: errorMessage(error, response.status) });
    reload();
  }
  async function withdraw(i: Invite) {
    const { error, response } = await api.DELETE('/api/workspaces/{ws_id}/invites/{invite_id}', {
      params: { path: { ws_id: ws, invite_id: i.id } },
    });
    setOutcome(response.ok ? { ok: true, text: `Withdrew the invite to ${i.email}.` } : { ok: false, text: errorMessage(error, response.status) });
    reload();
  }

  return (
    <section id="members" aria-labelledby="h-members" className={SECTION}>
      <h2 id="h-members" className={H2}>
        Members
      </h2>
      <p className={SUB}>Viewers see every card. Owners add meta repos and members. Everyone signs in with {signInProviderName()}.</p>
      {list.state === 'loading' && <p className="text-ink-muted">Loading…</p>}
      {list.state === 'error' && <Status outcome={{ ok: false, text: list.message }} />}
      {list.state === 'ok' && (
          <Table>
            <thead>
              <tr>
                <Th>Name</Th>
                <Th>Role</Th>
                <Th>
                  <span className="sr-only">Actions</span>
                </Th>
              </tr>
            </thead>
            <tbody>
              {list.data.members.map((m) => {
                const label = m.name ?? m.email ?? `user ${m.user_id}`;
                return (
                  <tr key={`m${m.user_id}`}>
                    <Td>
                      <span className={WHO}>
                        <span className={AVATAR} aria-hidden="true">
                          {initials(label)}
                        </span>
                        <span>
                          {label}
                          {m.user_id === me && <span className="text-ink-muted"> (you)</span>}
                          {m.email && <small className={SMALL}>{m.email}</small>}
                        </span>
                      </span>
                    </Td>
                    <Td>
                      {owner ? (
                        <Select
                          className={ROLE}
                          aria-label={`Role of ${label}`}
                          value={m.role}
                          onChange={(e) => void changeRole(m, e.target.value as Role)}
                        >
                          <option value="owner">owner</option>
                          <option value="viewer">viewer</option>
                        </Select>
                      ) : (
                        m.role
                      )}
                    </Td>
                    <Td className="text-right">
                      {owner && (
                        <Button variant="ghost" onClick={() => void removeMember(m)} aria-label={`Remove ${label}`}>
                          Remove
                        </Button>
                      )}
                    </Td>
                  </tr>
                );
              })}
              {list.data.invites.map((i) => (
                <tr key={`i${i.id}`}>
                  <Td>
                    <span className={WHO}>
                      <span className={AVATAR} aria-hidden="true">
                        {initials(i.email)}
                      </span>
                      <span>
                        {i.email}
                        <small className={SMALL}>invited{i.created_at ? ` ${ago(i.created_at)}` : ''} · joins at first sign-in</small>
                      </span>
                    </span>
                  </Td>
                  <Td>{i.role}</Td>
                  <Td className="text-right">
                    {owner && (
                      <Button variant="ghost" onClick={() => void withdraw(i)} aria-label={`Withdraw invite to ${i.email}`}>
                        Withdraw
                      </Button>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
      )}
      {owner && (
        <form className={FORM} onSubmit={add}>
          <label className="sr-only" htmlFor="invite-email">
            {signInEmailLabel()}
          </label>
          <Input
            id="invite-email"
            sans
            className={FORM_INPUT}
            type="email"
            placeholder={signInEmailLabel()}
            value={email}
            required
            onChange={(e) => setEmail(e.target.value)}
          />
          <label className="sr-only" htmlFor="invite-role">
            Role
          </label>
          <Select id="invite-role" className={ROLE} value={role} onChange={(e) => setRole(e.target.value as Role)}>
            <option value="viewer">viewer</option>
            <option value="owner">owner</option>
          </Select>
          <Button type="submit" variant="primary" disabled={!email.trim()}>
            Add member
          </Button>
        </form>
      )}
      <Status outcome={outcome} />
    </section>
  );
}

const NAV_GROUP =
  'mt-3 px-2.5 pb-1 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-ink-muted first:mt-0 narrow:mt-0 narrow:basis-full';

function FleetAndCi({ ws }: { ws: number }) {
  const [fleet, reload] = useFleet(ws);
  return (
    <>
      <h2 className="m-0 -mb-4 border-t border-hairline pt-6 text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-muted">
        Fleet and CI
      </h2>
      <GitHubAppSection ws={ws} />
      <HostsSection ws={ws} fleet={fleet} reload={reload} />
      <RunnersSection ws={ws} fleet={fleet} reload={reload} />
      <AlertsSection ws={ws} />
    </>
  );
}

export function AdminPage() {
  const ws = Number(useParams().ws);
  const { me, reload } = useSession();
  const workspace = findWorkspace(me, ws);
  if (!workspace) return <NotFoundPage />;
  const owner = workspace.role === 'owner';
  return (
    <Shell ws={ws}>
      <div className="grid grid-cols-[200px_minmax(0,860px)] justify-center gap-10 px-5 pb-12 pt-7 narrow:grid-cols-[minmax(0,1fr)] narrow:gap-5 phone:px-3 phone:pb-8 phone:pt-4">
        <nav
          aria-label="Settings sections"
          className="sticky top-[72px] flex flex-col gap-0.5 self-start narrow:static narrow:flex-row narrow:flex-wrap [&>a]:rounded-[6px] [&>a]:px-2.5 [&>a]:py-1.5 [&>a]:text-[12.5px] [&>a]:text-ink-body [&>a:hover]:bg-surface [&>a:hover]:no-underline"
        >
          <span className={NAV_GROUP}>Workspace</span>
          <a href="#general">General</a>
          <a href="#repos">Meta repos</a>
          <a href="#members">Members</a>
          {owner && (
            <>
              <span className={NAV_GROUP}>Fleet and CI</span>
              <a href="#github">GitHub App</a>
              <a href="#hosts">Hosts</a>
              <a href="#runners">Runners</a>
              <a href="#alerts">Alerts</a>
            </>
          )}
        </nav>
        <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-9">
          <PageHeader
            title="Workspace settings"
            purpose={
              <>
                {workspace.name} · you are {owner ? 'an owner' : 'a viewer'}
                {owner ? '' : '. Only owners can change these settings.'}
              </>
            }
          />
          <General key={workspace.name} ws={ws} name={workspace.name} owner={owner} onSaved={reload} />
          <Repos ws={ws} owner={owner} />
          <Members ws={ws} owner={owner} me={me.user.id} />
          {owner && <FleetAndCi ws={ws} />}
        </div>
      </div>
    </Shell>
  );
}
